import React, { useEffect, useState } from 'react';
import { Phone, MapPin, Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
type NavItem = {
  name: string;
  href: string;
  dropdown?: {
    name: string;
    href: string;
  }[];
};
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  const navItems: NavItem[] = [{
    name: 'Home',
    href: '/'
  }, {
    name: 'In-Store Selection',
    href: '/store-tour',
    dropdown: [{
      name: 'Firearms',
      href: '/firearms'
    }, {
      name: 'Ammunition',
      href: '/ammunition'
    }, {
      name: 'Optics',
      href: '/optics'
    }, {
      name: 'Reloading Supplies',
      href: '/reloading-supplies'
    }, {
      name: 'Safes & Accessories',
      href: '/safes-accessories'
    }, {
      name: 'Hunting Gear & Blinds',
      href: '/hunting-gear-blinds'
    }]
  }, {
    name: 'Services & Classes',
    href: '/services',
    dropdown: [{
      name: 'Gunsmithing & In-Store Support',
      href: '/services'
    }, {
      name: 'Minnesota Permit to Carry Classes',
      href: '/mn-permit-to-carry-classes'
    }, {
      name: 'NFA / Form 4 In-Store Support',
      href: '/services'
    }, {
      name: 'Questions & Transfers',
      href: '/services'
    }]
  }, {
    name: 'Store Tour',
    href: '/store-tour'
  }, {
    name: 'Events',
    href: '/events'
  }, {
    name: 'FAQ',
    href: '/faq'
  }, {
    name: 'Contact',
    href: '/contact'
  }];
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-brand-offwhite shadow-md py-2' : 'bg-brand-offwhite/95 py-4'}`} data-id="element-66">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-67">
        <div className="flex justify-between items-center" data-id="element-68">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-id="element-69">
            <img src="https://drwzpk38qkpfb.cloudfront.net/www.ksportsllc.com/uploaded/images/Updated-logo.png?opt=w3" alt="Kjergaard Sports logo" className="h-12 md:h-16 w-auto" data-id="element-70" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center space-x-8" data-id="element-71">
            {navItems.map(item => <div key={item.name} className="relative group" onMouseEnter={() => setActiveDropdown(item.name)} onMouseLeave={() => setActiveDropdown(null)} data-id="element-72">
                <Link to={item.href} className={`text-brand-charcoal hover:text-brand-hunter font-medium text-sm flex items-center transition-colors ${location.pathname === item.href ? 'text-brand-hunter' : ''}`} data-id="element-73">
                  {item.name}
                  {item.dropdown && <ChevronDown className="ml-1 w-4 h-4" data-id="element-74" />}
                </Link>

                {/* Dropdown */}
                {item.dropdown && <AnimatePresence data-id="element-75">
                    {activeDropdown === item.name && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 mt-2 w-64 bg-brand-cream border border-brand-bone shadow-lg rounded-sm overflow-hidden" data-id="element-76">
                        <div className="py-2" data-id="element-77">
                          {item.dropdown.map(dropItem => <Link key={dropItem.name} to={dropItem.href} className="block px-4 py-2 text-sm text-brand-charcoal hover:bg-brand-bone hover:text-brand-hunter transition-colors" data-id="element-78">
                              {dropItem.name}
                            </Link>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>}
              </div>)}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center space-x-4" data-id="element-79">
            <a href="tel:507-368-4696" className="flex items-center text-brand-hunter font-medium hover:text-brand-wood transition-colors" data-id="element-80">
              <Phone className="w-4 h-4 mr-2" data-id="element-81" />
              507-368-4696
            </a>
            <Link to="/contact" className="bg-brand-hunter text-brand-offwhite px-5 py-2.5 rounded-sm font-medium text-sm hover:bg-brand-wood transition-colors flex items-center" data-id="element-82">
              <MapPin className="w-4 h-4 mr-2" data-id="element-83" />
              Get Directions
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="flex lg:hidden items-center space-x-4" data-id="element-84">
            <a href="tel:507-368-4696" className="text-brand-hunter p-2" aria-label="Call store" data-id="element-85">
              <Phone className="w-6 h-6" data-id="element-86" />
            </a>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-brand-charcoal p-2" aria-label="Toggle menu" data-id="element-87">
              {mobileMenuOpen ? <X className="w-7 h-7" data-id="element-88" /> : <Menu className="w-7 h-7" data-id="element-89" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-90">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: '100vh'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="lg:hidden absolute top-full left-0 right-0 bg-brand-offwhite border-t border-brand-bone overflow-y-auto pb-32" data-id="element-91">
            <div className="px-4 py-6 space-y-6" data-id="element-92">
              {navItems.map(item => <div key={item.name} className="border-b border-brand-bone pb-4" data-id="element-93">
                  <Link to={item.href} className="text-lg font-serif text-brand-charcoal font-medium block" data-id="element-94">
                    {item.name}
                  </Link>
                  {item.dropdown && <div className="mt-3 pl-4 space-y-3 border-l-2 border-brand-bone" data-id="element-95">
                      {item.dropdown.map(dropItem => <Link key={dropItem.name} to={dropItem.href} className="block text-brand-charcoal/80" data-id="element-96">
                          {dropItem.name}
                        </Link>)}
                    </div>}
                </div>)}
              <div className="pt-4 space-y-4" data-id="element-97">
                <Link to="/contact" className="w-full bg-brand-hunter text-brand-offwhite px-5 py-3 rounded-sm font-medium flex items-center justify-center" data-id="element-98">
                  <MapPin className="w-5 h-5 mr-2" data-id="element-99" />
                  Get Directions
                </Link>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}